// Cycle 410 — the lead window's takeover: an urgent low calls before it
// shouts.
//
// Mounted by NowScreen while a LEAD WINDOW is open on the active member's
// urgent low — the phone calls are running, and this device is holding the
// urgent audio UPGRADE until the deadline on its own clock. The screen still
// takes over and flashes; the number is live; the countdown says how long
// until the alarm sounds; and the two things a caregiver can do are both on
// screen at all times.
//
// ⚠ THE LEAD WINDOW SUPPRESSES LOCAL ALARM AUDIO AND THE REPEATING CRITICAL
// PUSH, AND NOTHING ELSE. This screen is where that is VISIBLE — it gates
// nothing itself. It reverts to the ordinary takeover the instant the
// window closes (`episode.audioLead` falls).
//
// THE COPY NAMES NO HERO, NO CONTACT AND NO PHONE NUMBER. Cycle 407 kept
// identity out of these frames on purpose; this component never reads
// `treatingContactName` or any contact list, and a source guard pins it.
//
// SNOOZE IS PRESENT IF AND ONLY IF AUDIO IS CURRENTLY SOUNDING for this
// member (`episode.audible`). On a cold crossing nothing sounds, so Snooze
// is absent and the reflex tap that produced three snoozes on 2026-08-31 is
// simply not reachable. On a descent the `low` audio is still sounding, so
// Snooze stays — the "at least one control on screen whenever audio sounds"
// floor is not negotiable, and this is how it is honoured. Stop is absent on
// urgent low, as always (cycle 73).
//
// FLASH: 1 Hz, `steps(1, end)` — a hard ceiling of 3 Hz is pinned by a
// source guard (photosensitivity). Not configurable.

import { TrendArrow } from "../glucose/TrendArrow";
import type { AlarmEpisodeState } from "../../hooks/useAlarmEpisode";
import type { FrameVariant } from "../../hooks/useFrameVariant";
import { AUDIO_LEAD_MS, fmtLeadRemaining } from "../../lib/audibleRow";
import {
  displayValue,
  fmtAge,
  trendInfo,
  zoneColor,
  zoneOf,
} from "../../lib/glucose";
import type { DisplayGlucose } from "../../lib/currentGlucose";
import type { Thresholds } from "../../lib/types";
import styles from "./AudioLeadScreen.module.css";

interface Props {
  variant: FrameVariant;
  memberName: string | null;
  /** ⚠ Cycle 435: the number is a selector-released `DisplayGlucose` or
   *  null (withheld). ONLY the number — the ring, the buttons and the lead
   *  window itself come from `episode`. A raw `Reading` cannot be passed. */
  current: DisplayGlucose | null;
  /** The age of the reading the selector judged, current or withheld. */
  ageMinutes: number | null;
  units: "mg/dL" | "mmol/L";
  thresholds: Thresholds;
  episode: AlarmEpisodeState;
  now: number;
  /** "I've got <Name>" — the SAME `beginTreating` door the takeover's
   *  declaration button uses (NowScreen owns it and its busy/error). */
  onDeclare: () => void;
  declareBusy?: boolean;
  declareError?: string | null;
  /** "Sound the alarm now" — `episode.soundNow()`. One direction. */
  onSoundNow: () => void;
  /** Snooze — `episode.address()`, exactly as cycle 409 shipped it. Only
   *  rendered while `episode.audible`. */
  onSnooze: () => void;
}

const RING_R = 52;
const RING_C = 2 * Math.PI * RING_R;

/** "I've got the member" — the member's first name, or a neutral fallback. Never
 *  a hero's name: this is the CHILD being declared for. */
export function leadDeclareLabel(memberName: string | null | undefined): string {
  const first = (memberName ?? "").trim().split(/\s+/)[0];
  return first ? `I've got ${first}` : "I've got this";
}

/** The advisory line. Names no hero, no contact, no number. */
export function leadAdvisoryLine(remainingMs: number, lowStillSounding: boolean): string {
  const clock = fmtLeadRemaining(remainingMs);
  return lowStillSounding
    ? `Calling your heroes — the low alarm keeps sounding. Full alarm in ${clock}`
    : `Calling your heroes — alarm in ${clock}`;
}

export function AudioLeadScreen({
  variant,
  memberName,
  current,
  ageMinutes,
  units,
  thresholds,
  episode,
  onDeclare,
  declareBusy = false,
  declareError = null,
  onSoundNow,
  onSnooze,
}: Props) {
  // Cycle 435: `sgv == null` is exactly "the selector released no current
  // value" — the number is withheld; nothing else on this screen reads it.
  const sgv = current?.sgv ?? null;
  const zone = zoneOf(sgv, thresholds);
  const color = sgv == null ? "var(--ink-3)" : zoneColor(zone);
  const valueText = sgv != null ? displayValue(sgv, units) : "—";
  const trend = trendInfo(current?.trend ?? null, current?.trendRate ?? null);
  const ageMin = ageMinutes;

  const remainingMs = episode.audioLeadRemainingMs ?? 0;
  const frac = Math.max(0, Math.min(1, remainingMs / AUDIO_LEAD_MS));
  const dashOffset = RING_C * (1 - frac);
  // The descent: something is already sounding (the `low` sibling).
  const lowStillSounding = episode.audible;
  const name = memberName && memberName.trim() ? memberName.trim() : null;

  return (
    <div
      className={`${styles.root} ${styles[variant] ?? ""}`}
      data-testid="lead-screen"
      role="alertdialog"
      aria-label={name ? `Urgent low · ${name}` : "Urgent low"}
      data-audible={lowStillSounding ? "1" : "0"}
    >
      <div className={styles.flash} aria-hidden data-testid="lead-flash" />
      <div className={styles.top}>
        <div className={styles.tag}>
          <span className={styles.tagDot} aria-hidden />
          Urgent low
        </div>
        <div className={styles.who}>{name ?? "—"}</div>
      </div>

      <div className={styles.main}>
        <div className={styles.glucose} data-testid="lead-glucose">
          <div className={`${styles.value} tnum`} style={{ color }}>
            {valueText}
          </div>
          <TrendArrow
            glyph={trend.glyph}
            zone={zone}
            size={variant === "kiosk" ? 120 : 72}
            stale={sgv == null}
            weight={700}
          />
        </div>
        <div className={styles.ring} data-testid="lead-ring">
          <svg className={styles.ringSvg} viewBox="0 0 120 120" aria-hidden>
            <circle className={styles.ringTrack} cx="60" cy="60" r={RING_R} />
            <circle
              className={styles.ringArc}
              cx="60"
              cy="60"
              r={RING_R}
              strokeDasharray={RING_C}
              strokeDashoffset={dashOffset}
            />
          </svg>
          <div className={styles.ringLabel}>
            <span className={`${styles.ringTime} tnum`} data-testid="lead-countdown">
              {fmtLeadRemaining(remainingMs)}
            </span>
            <span className={styles.ringSub}>to alarm</span>
          </div>
        </div>
      </div>

      <div className={styles.meta}>
        <span>{sgv == null ? "No recent reading" : trend.label}</span>
        {ageMin != null && (
          <>
            <span className={styles.metaDot} aria-hidden />
            <span className="tnum">{fmtAge(ageMin)}</span>
          </>
        )}
      </div>

      <div className={styles.advisory} data-testid="lead-advisory" role="status">
        {declareError ?? leadAdvisoryLine(remainingMs, lowStillSounding)}
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          className={`${styles.action} ${styles.declare}`}
          data-testid="lead-declare"
          disabled={declareBusy}
          onClick={onDeclare}
        >
          {declareBusy ? "Starting…" : leadDeclareLabel(memberName)}
        </button>
        <button
          type="button"
          className={`${styles.action} ${styles.soundNow}`}
          data-testid="lead-sound-now"
          onClick={onSoundNow}
        >
          Sound the alarm now
        </button>
        {/* Snooze iff audio is sounding (the descent). Stop: never. */}
        {episode.audible && (
          <button
            type="button"
            className={`${styles.action} ${styles.snooze}`}
            data-testid="lead-snooze"
            onClick={onSnooze}
          >
            Snooze
          </button>
        )}
      </div>
    </div>
  );
}
